import { useState, useMemo, Fragment, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Transition } from '@headlessui/react';
import classNames from 'classnames';

import { useMainMachine } from '../../../utils/useMainMachine';
import { useCrazyAI } from '../../../ai/useCrazyAI';
import { parseVariations } from '../../../ai/variations';
import { usePrevious } from '../../../utils/usePrevious';
import { DisclosureButton } from '../basic-ui/DisclosureButton';
import { Button } from '../basic-ui/Button';
import { Spinner } from '../basic-ui/Spinner';
import { TextSuggestion } from './TextSuggestions';
import { STATES, ACTIONS } from '../../../state';
import { getHTMLSuggestionContent, isStringHTML, removeSVGs, restoreSVGs } from '../../../utils/html';
import { inflect } from '../../../utils/string';
import { sendMetric } from '../../../utils/metrics';
import { AI_FEEDBACK_TYPES, SUGGESTION_COUNT, METRICS_PREFIX, AI_PROMPTS } from '../../constants';

import HideIcon from '@crazyegginc/hatch/dist/images/icon-unwatched-outline.svg?react';
import SparklesIcon from '@crazyegginc/hatch/dist/images/icon-sparkles-filled.svg?react';
import BadgeCrossIcon from '@crazyegginc/hatch/dist/images/icon-badge-cross.svg?react';
import BadgeTickIcon from '@crazyegginc/hatch/dist/images/icon-badge-tick.svg?react';
import BadgeWarningIcon from '@crazyegginc/hatch/dist/images/icon-badge-warning.svg?react';
import { formatChecksForAI } from '../../../utils/utils';

export function BrandCheckResult({ contentVisible }) {
  const { useSelector } = useMainMachine();
  const visible = useSelector((state) => state.matches({ [STATES.INITIALIZED]: { [STATES.EDITOR]: STATES.EDIT } }));
  const configured = useSelector(({ context }) => Boolean(context.brandGuidelines?.updatedAt));
  const selectedElement = useSelector(({ context }) => context.selectedElement);

  if (!visible || !contentVisible || !configured) return null;

  if (!selectedElement.isCheckable) {
    return (
      <div className="flex items-start font-semibold text-white mt-8" data-testid="brand-check-results">
        <BadgeWarningIcon className="w-4 h-4 fill-current mr-2.5 mt-1 shrink-0" />
        This element cannot be checked for brand compliance.
      </div>
    );
  }

  return <BrandCheckResultSection />;
}

function BrandCheckResultSection() {
  const { useSelector, machine } = useMainMachine();
  const selectedElement = useSelector(({ context }) => context.selectedElement);
  const open = useSelector(({ context }) => context.brandCheckResultsOpen);
  const failedChecks = useSelector(({ context }) =>
    context.brandCheckResults
      ?.filter((e) => e.selector === selectedElement.selector)
      .filter(
        (e) =>
          !Object.entries(context.editor.pages.find((p) => p.id === context.activePageId).ignoredChecks).some(
            ([c, s]) => e.check_id === c && s.includes(e.selector),
          ),
      ),
  );
  const isLoading = useSelector(({ context }) => Object.values(context.runningCheckRefs).filter(Boolean).length > 0);

  return (
    <div className="flex flex-col mt-8" data-testid="brand-check-results">
      <div className="flex items-center justify-between">
        {isLoading ? (
          <div className="flex items-center space-x-2.5">
            <Spinner className="w-3.5 h-3.5" />
            <span>Checking guidelines...</span>
          </div>
        ) : (
          <>
            {failedChecks?.length > 0 ? (
              <>
                <div className="font-semibold flex items-center">
                  <div className="relative mr-2.5">
                    <BadgeCrossIcon className="text-[#FF993B] w-4 h-4 fill-current z-10 relative" />
                    <div className="absolute rounded-full bg-white w-3 h-2.5 top-0.5 left-0.5 z-0" />
                  </div>
                  {failedChecks.length} {inflect('issue', failedChecks.length)} in this element
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      failedChecks.forEach((check) =>
                        machine.send({
                          type: ACTIONS.IGNORE_CHECKS,
                          checkId: check.check_id,
                          el: selectedElement.selector,
                        }),
                      );
                    }}
                    className="p-1 text-gray-200 hover:text-white"
                  >
                    <HideIcon className="w-4 h-4 fill-current" aria-label="ignore all checks" />
                  </button>
                  <DisclosureButton
                    open={open}
                    onClick={() => machine.send({ type: ACTIONS.TOGGLE_BRANDCHECKSRESULTS })}
                    contentId="controls-content"
                  />
                </div>
              </>
            ) : (
              <div className="flex items-center">
                <div className="relative mr-2.5">
                  <BadgeTickIcon className="w-4 h-4 fill-current text-green-500 z-10 relative" />
                  <div className="absolute rounded-full bg-white w-3 h-2.5 top-0.5 left-0.5 z-0" />
                </div>
                Brand compliant
              </div>
            )}
          </>
        )}
      </div>
      <div className="overflow-hidden">
        <motion.div
          initial={false}
          animate={
            open
              ? {
                  height: 'auto',
                  opacity: 1,
                }
              : {
                  height: 0,
                  opacity: 0,
                }
          }
          transition={{ duration: 0.4, ease: 'linear' }}
          className="mt-2.5 flex flex-col"
          id="controls-content"
          data-testid="controls-content"
        >
          <BrandCheckResultContent failedChecks={failedChecks} isLoading={isLoading} key={selectedElement.selector} />
        </motion.div>
      </div>
    </div>
  );
}

function BrandCheckResultContent({ failedChecks, isLoading }) {
  const { useSelector, machine } = useMainMachine();

  const [showSuggestions, setShowSuggestions] = useState(false);
  const checks = useSelector(({ context }) => context.brandGuidelines.checks);
  const selectedElement = useSelector(({ context }) => context.selectedElement);
  const [selectedSuggestion, setSelectedSuggestion] = useState();
  const [suggestions, setSuggestions] = useState();
  const [retries, setRetries] = useState(0);
  const stableSelectedSuggestion = usePrevious(selectedSuggestion);
  const { html, text, placeholder } = selectedElement;
  const isInput = selectedElement.tagName === 'INPUT';
  const isHTML = isInput ? false : isStringHTML(html);
  let content;
  if (isInput) {
    content = placeholder;
  } else {
    content = isHTML ? html : text;
  }
  const [stableContent, setStableContent] = useState(content);
  const [queryStartTime, setQueryStartTime] = useState(null);
  const anonymousId = useSelector(({ context }) => context.anonymousId);
  const userId = useSelector(({ context }) => context.editor.user_id);

  useEffect(() => {
    // If element is edited manually in the other section, we "reset" here. This is a bit hacky.
    if (removeSVGs(content) !== stableSelectedSuggestion.current && content !== stableContent) {
      setShowSuggestions(false);
      setStableContent(content);
      setSuggestions(null);
      setRetries(0);
    }
  }, [content, stableSelectedSuggestion, stableContent]);

  const params = useMemo(() => {
    const result = {
      text: removeSVGs(stableContent),
      suggestion_count: SUGGESTION_COUNT,
      checks: JSON.stringify((checks?.filter((check) => check.enabled === true) ?? []).map(formatChecksForAI)),
    };
    return result;
  }, [stableContent, checks]);

  const context = useMemo(
    () => ({
      url: `${location.pathname}${location.search}`,
    }),
    [],
  );

  const { data, sendFeedback, refetch, error } = useCrazyAI({
    type: AI_PROMPTS.SUGGESTED_FIXES,
    params,
    context,
    enabled: showSuggestions,
  });

  useEffect(() => {
    if (!data?.ready && !queryStartTime && showSuggestions) {
      setQueryStartTime(performance.now());
    } else if (data?.ready && !error && queryStartTime) {
      sendMetric(`${METRICS_PREFIX}_element_fixes_suggested`, {
        anonymous_id: anonymousId,
        user_id: userId,
        elapsed_time: Math.round(performance.now() - queryStartTime),
      });
      setQueryStartTime(null);
    } else if (error) {
      setQueryStartTime(null);
    }
  }, [data, error, queryStartTime, userId, anonymousId, showSuggestions]);

  useEffect(() => {
    if (error) {
      machine.send({ type: ACTIONS.TEXT_GENERATION_ERROR, error, params });
      setShowSuggestions(false);
      setSuggestions(null);
      setRetries(0);
    }
  }, [error, machine, params]);

  useEffect(() => {
    if (!data?.ready && !data?.text) return;

    try {
      setSuggestions(
        parseVariations(data.text, {
          partial: !data.ready,
          isRenderable: (suggestion) => !isHTML || Boolean(getHTMLSuggestionContent(suggestion).trim()),
        }),
      );
    } catch {
      if (data.ready && retries < 3) {
        refetch();
        setRetries((x) => x + 1);
      } else if (data.ready) {
        setSuggestions(null);
      }
    }
  }, [data, isHTML, refetch, retries]);

  function applySuggestion(suggestion) {
    setSelectedSuggestion(suggestion);
    if (isInput) {
      machine.send({
        type: ACTIONS.SET_SELECTED_EL_ATTRIBUTE,
        attribute: 'placeholder',
        value: suggestion,
      });
    } else if (isHTML) {
      const newHTML = restoreSVGs(stableContent, suggestion);
      machine.send({ type: ACTIONS.SET_SELECTED_EL_HTML, html: newHTML, ai_fix: true });
    } else {
      machine.send({ type: ACTIONS.SET_SELECTED_EL_TEXT, text: suggestion, ai_fix: true });
    }

    sendFeedback(AI_FEEDBACK_TYPES.IMPLICIT_POSITIVE);
  }

  function revertSuggestion() {
    setSelectedSuggestion(null);
    if (isInput) {
      machine.send({
        type: ACTIONS.SET_SELECTED_EL_ATTRIBUTE,
        attribute: 'placeholder',
        value: stableContent,
      });
    } else if (isHTML) {
      machine.send({ type: ACTIONS.SET_SELECTED_EL_HTML, html: stableContent });
    } else {
      machine.send({ type: ACTIONS.SET_SELECTED_EL_TEXT, text: stableContent });
    }
  }

  return (
    <div className="flex flex-col">
      {(failedChecks ?? []).map((c) => (
        <div key={c.checkName} className="mb-3.75">
          <div className="text-white font-semibold flex justify-between">
            <div className="truncate mr-1">{c.checkName}</div>
            <button
              type="button"
              onClick={() => {
                machine.send({ type: ACTIONS.IGNORE_CHECKS, checkId: c.check_id, el: selectedElement.selector });
              }}
              className="p-1 text-gray-200 hover:text-white mr-7"
            >
              <HideIcon className="w-4 h-4 fill-current" aria-label="ignore check" />
            </button>
          </div>
          <div className="text-gray-200">{c.reason}</div>
        </div>
      ))}
      {showSuggestions ? (
        <div data-testid="ai-suggestions" className="mb-3.75 flex flex-col">
          <>
            {suggestions?.map((suggestion) => (
              <TextSuggestion
                key={suggestion}
                onClick={() => (selectedSuggestion === suggestion ? revertSuggestion() : applySuggestion(suggestion))}
                selected={selectedSuggestion === suggestion}
                anySelected={!!selectedSuggestion}
                color="184 158 255"
                disabled={!data?.ready}
              >
                {isHTML ? getHTMLSuggestionContent(suggestion) : suggestion}
              </TextSuggestion>
            ))}
            {!data?.ready
              ? Array.from({ length: SUGGESTION_COUNT - (suggestions?.length ?? 0) }).map((x, i) => (
                  <div
                    key={SUGGESTION_COUNT - i}
                    className={classNames(
                      'w-[240px] h-[70px] mt-2.5 rounded-md bg-gray-500/40 animate-skeleton',
                      'bg-gradient-to-r from-gray-500/40 via-[20%] via-gray-500/10 to-[40%] to-gray-500/40 bg-[length:600px]',
                    )}
                  />
                ))
              : null}
          </>
        </div>
      ) : null}
      <Transition
        show={Boolean(data?.ready || (!showSuggestions && failedChecks?.length > 0 && !isLoading))}
        enter="transition-opacity duration-700 delay-500"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-0"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        as={Fragment}
      >
        <Button
          onClick={() => {
            if (!showSuggestions) {
              setShowSuggestions(true);
            } else {
              setRetries(0);
              setSelectedSuggestion(null);
              refetch();
            }
          }}
        >
          <SparklesIcon className="w-4 h-4 fill-current mr-2" />
          {showSuggestions ? 'More suggested fixes' : 'Suggest fixes'}
        </Button>
      </Transition>
    </div>
  );
}
